import React, { useRef, useEffect, useState } from "react";
import api from "../../services/api";
import { Button, Typography, IconButton, Slider } from "@material-ui/core";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import MicIcon from "@material-ui/icons/Mic";
import TranslateIcon from "@material-ui/icons/Translate";
import { getBackendUrl } from "../../config";

const LS_NAME = "audioMessageRate";

const useStyles = makeStyles((theme) => ({
  audioContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "340px",
    minWidth: "260px",
    padding: "6px 8px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
    borderRadius: "18px",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
    boxSizing: "border-box",
  },
  playerRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: "8px",
  },
  playButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    width: "36px",
    height: "36px",
    minWidth: "36px",
    padding: 0,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  micAvatar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    opacity: 0.8,
  },
  sliderContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0 4px",
  },
  slider: {
    padding: "8px 0",
    color: theme.palette.primary.main,
    "& .MuiSlider-thumb": {
      width: 12,
      height: 12,
      marginTop: -4,
      marginLeft: -6,
      boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
    },
    "& .MuiSlider-track": {
      height: 4,
      borderRadius: 2,
    },
    "& .MuiSlider-rail": {
      height: 4,
      borderRadius: 2,
      opacity: 0.25,
    },
  },
  timeRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "-4px",
  },
  timeText: {
    fontSize: "0.72rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontFamily: "monospace",
  },
  rateChip: {
    fontSize: "0.7rem",
    fontWeight: "bold",
    minWidth: "auto",
    padding: "2px 6px",
    height: "22px",
    borderRadius: "11px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
    color: theme.palette.text.primary,
    textTransform: "none",
    border: "none",
    lineHeight: 1,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
    },
  },
  transcriptionContainer: {
    width: "100%",
    marginTop: "6px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  transcriptionText: {
    fontSize: "0.82rem",
    lineHeight: 1.4,
    wordBreak: "break-word",
    padding: "6px 10px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#ffffff",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    width: "100%",
    boxSizing: "border-box",
  },
  transcribeButton: {
    fontSize: "0.72rem",
    padding: "2px 10px",
    minWidth: "auto",
    height: "26px",
    textTransform: "none",
    borderRadius: "13px",
    boxShadow: "none",
  },
}));

const formatTime = (seconds) => {
  if (isNaN(seconds) || seconds === 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const AudioModal = ({ url, message, disableTranscription = false }) => {
  const theme = useTheme();
  const classes = useStyles();
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioRate, setAudioRate] = useState(
    parseFloat(localStorage.getItem(LS_NAME) || "1")
  );
  const [transcription, setTranscription] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const body = message?.body ?? "";
  const transcrito = message?.transcrito ?? false;

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = audioRate;
      localStorage.setItem(LS_NAME, audioRate);
    }
  }, [audioRate]);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, [url]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((err) => {
        console.error("Erro ao dar play no áudio:", err);
      });
    }
  };

  const handleSliderChange = (event, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const toggleRate = () => {
    let newRate = 1;
    if (audioRate === 1) newRate = 1.5;
    else if (audioRate === 1.5) newRate = 2;
    else if (audioRate === 2) newRate = 0.5;
    else newRate = 1;

    setAudioRate(newRate);
  };

  const getAudioSource = () => {
    let sourceUrl = url;
    let sourceType = "audio/mpeg";

    if (!sourceUrl) {
      return { src: null, type: sourceType };
    }

    if (!sourceUrl.startsWith("http")) {
      if (sourceUrl.startsWith("/public")) {
        sourceUrl = `${getBackendUrl()}${sourceUrl}`;
      } else if (sourceUrl.startsWith("/")) {
        sourceUrl = `${getBackendUrl()}${sourceUrl}`;
      } else {
        sourceUrl = `${getBackendUrl()}/public/${sourceUrl}`;
      }
    }

    const extension = sourceUrl.split("?")[0].split(".").pop()?.toLowerCase();
    if (extension === "ogg") {
      sourceType = "audio/ogg";
    } else if (extension === "mp3") {
      sourceType = "audio/mpeg";
    } else if (extension === "wav") {
      sourceType = "audio/wav";
    } else if (extension === "aac") {
      sourceType = "audio/aac";
    } else if (extension === "m4a" || extension === "mp4") {
      sourceType = "audio/mp4";
    }

    if (isIOS && sourceUrl.endsWith(".ogg")) {
      sourceUrl = sourceUrl.replace(".ogg", ".mp3");
      sourceType = "audio/mpeg";
    }

    return { src: sourceUrl, type: sourceType };
  };

  const handleTranscribe = async () => {
    setIsTranscribing(true);
    try {
      const audioData = { wid: message.wid };
      const { data } = await api.post(`/message/transcribeAudio`, audioData);
      if (data) {
        setTranscription(data);
      }
    } catch (error) {
      console.error("Erro ao transcrever áudio:", error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const audioSource = getAudioSource();

  return (
    <div className={classes.audioContainer}>
      {/* Hidden HTML5 Audio Element for playback control */}
      <audio
        ref={audioRef}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
          }
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            setDuration(audioRef.current.duration || 0);
            audioRef.current.playbackRate = audioRate;
          }
        }}
      >
        {audioSource.src && <source src={audioSource.src} type={audioSource.type} />}
        {audioSource.src && <source src={audioSource.src} />}
      </audio>

      {/* Custom Modern WhatsApp-Style Audio Player Row */}
      <div className={classes.playerRow}>
        <IconButton
          className={classes.playButton}
          onClick={togglePlayPause}
          size="small"
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>

        <div className={classes.micAvatar}>
          <MicIcon fontSize="small" />
        </div>

        <div className={classes.sliderContainer}>
          <Slider
            className={classes.slider}
            value={currentTime}
            max={duration || 100}
            step={0.1}
            onChange={handleSliderChange}
          />
          <div className={classes.timeRow}>
            <Typography className={classes.timeText}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </div>
        </div>

        <Button
          className={classes.rateChip}
          onClick={toggleRate}
          size="small"
          disableRipple
        >
          {audioRate}x
        </Button>
      </div>

      {/* Transcription Area */}
      {!disableTranscription && (
        <div className={classes.transcriptionContainer}>
          {!transcrito ? (
            transcription ? (
              <Typography className={classes.transcriptionText} variant="body2">
                <strong>Transcrição:</strong> {transcription}
              </Typography>
            ) : (
              <Button
                onClick={handleTranscribe}
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<TranslateIcon style={{ fontSize: "14px" }} />}
                className={classes.transcribeButton}
                disabled={isTranscribing}
              >
                {isTranscribing ? "Transcrevendo..." : "Transcrever"}
              </Button>
            )
          ) : (
            <Typography className={classes.transcriptionText} variant="body2">
              <strong>Transcrição:</strong> {body}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default AudioModal;